import dayjs, {Dayjs} from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import customParseFormat from "dayjs/plugin/customParseFormat";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);

export const ISO_FORMAT = "YYYY-MM-DD[T]HH:mm:ss";
export const DATE_FORMAT = "YYYY-MM-DD";
export const DATE_FORMAT_V2 = "MMM DD, YYYY";
export const DATE_FORMAT_V3 = "MM/DD/YYYY";
export const DATE_FORMAT_V4 = "MM/DD/YY";
export const MONTH_YEAR_FORMAT = "MM/YYYY";

export const TIMESTAMP_FORMAT_V5 = "MMM DD, YYYY hh:mm:ss A";
export const TIMESTAMP_FORMAT_V2 = "MM-DD-YY hh:mm:ss A";
export const SHORT_TIMESTAMP_FORMAT = "MMM DD, YYYY - hh:mm A";
export const SHORT_DATE_FORMAT = "MMM DD, YY";
export const TIME_FORMAT_12H = "h:mm A";
export const DATETIME_FORMAT_12H = "M/D/YYYY, h:mm:ss A";

export const TIMEZONE_ET = "America/New_York";

export const sortDatesDesc = (date1: string, date2: string) => {
  const d1 = new Date(date1);
  const d2 = new Date(date2);

  if (d1 > d2) return -1;
  if (d1 < d2) return 1;
  return 0;
};
export const extractDateFromString = (stringDate?: string, fullDate: boolean = false) => {
  if (!stringDate) return "N/A";
  const d = new Date(stringDate);
  const formattedDate = d.getMonth() + 1 + "/" + d.getDate() + "/" + d.getFullYear();
  return fullDate
    ? `${formattedDate}  ${d.getHours().toString().padStart(2, "0")}:${d.getMinutes().toString().padStart(2, "0")}:${d
        .getSeconds()
        .toString()
        .padStart(2, "0")}`
    : formattedDate;
};

const isMidnight = (d: Dayjs): boolean =>
  d.hour() === 0 && d.minute() === 0 && d.second() === 0 && d.millisecond() === 0;

const getDateFormat = (formatType: "date" | "datetime" | "time"): string => {
  switch (formatType) {
    case "datetime":
      return TIMESTAMP_FORMAT_V5;
    case "time":
      return TIME_FORMAT_12H;
    default:
      return DATE_FORMAT_V2;
  }
};

export const convertUTCDateToTimezone = (
  stringDate?: string | Date,
  formatType: "date" | "datetime" | "time" = "datetime",
  timezone: string = TIMEZONE_ET,
  suffix: string = "ET"
): string => {
  if (!stringDate) return "";
  const utcDate = dayjs.utc(stringDate);
  if (isMidnight(utcDate)) return utcDate.format(DATE_FORMAT_V2);
  return `${utcDate.tz(timezone).format(getDateFormat(formatType))} [${suffix}]`;
};

/**
 * Formats a Date to time string in 12-hour format with AM/PM
 * Example: "2:30 PM"
 */
export const formatTime = (date: Date): string => {
  return dayjs(date).format(TIME_FORMAT_12H);
};

/**
 * Formats a Date to full date and time string
 * Example: "6/9/2025, 4:29:38 PM"
 */
export const formatDateTime = (date: Date): string => {
  return dayjs(date).format(DATETIME_FORMAT_12H);
};

export const formatShortDate = (dateStr: string, inputFormat: string = DATE_FORMAT_V4): string => {
  const parsed = dayjs(dateStr, inputFormat);
  if (!parsed.isValid()) return dateStr;
  return parsed.format(DATE_FORMAT_V2);
};

export const toCardExpirationDate = (expirationMonth: string, expirationYear: string): Dayjs =>
  dayjs(`${expirationMonth}/${expirationYear}`, MONTH_YEAR_FORMAT, true);
